import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingBag } from 'lucide-react';
import LazyImage from './LazyImage';
import { useCurrency } from '../contexts/CurrencyContext';

const API = `${window.location.origin}/api`;

/**
 * "Frequently bought together" rail on the product page.
 *
 * Powered by /api/products/{id}/frequently-bought — a co-occurrence
 * aggregation over paid orders. The endpoint already filters out the
 * current product, returns up to N candidates, and orders them by
 * co-purchase count. Empty array = no signal yet; we just don't render.
 *
 * The user can click any tile to navigate, or hit the round + button to
 * push it straight into cart. Cart push uses the existing CartContext
 * so all of the price / variant logic stays in one place.
 */
export default function FrequentlyBought({ productId }) {
  const { formatPrice } = useCurrency();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    axios.get(`${API}/products/${productId}/frequently-bought?limit=4`)
      .then(r => {
        if (!cancelled) setItems(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => { cancelled = true; };
  }, [productId]);

  if (items.length === 0) return null;

  return (
    <section
      className="py-12 sm:py-16 border-t border-white/5 bg-[#0A0A0A]"
      data-testid="frequently-bought-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans flex items-center gap-2">
              <ShoppingBag className="w-3.5 h-3.5" /> Buyer pattern
            </p>
            <h2
              className="text-2xl sm:text-3xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Frequently Bought Together
            </h2>
            <p className="text-white/40 text-xs sm:text-sm font-sans mt-1">
              Other JS Fragrance customers loved pairing this with…
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-5">
          {items.map(p => (
            <Link
              key={p.id}
              to={`/product/${p.id}`}
              className="group block"
              data-testid={`fbt-item-${p.id}`}
            >
              <div className="aspect-[3/4] bg-[#141414] border border-white/5 overflow-hidden mb-2">
                <LazyImage
                  src={p.image_url}
                  alt={p.name}
                  className="w-full h-full opacity-85 group-hover:opacity-100 transition-opacity"
                />
              </div>
              <p className="text-white text-xs sm:text-sm leading-tight font-sans line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                {p.name}
              </p>
              <p className="text-[#D4AF37] text-xs sm:text-sm font-sans mt-1">
                {formatPrice(p.price || p.base_price)}
              </p>
              <p className="text-white/30 text-[10px] uppercase tracking-[0.18em] font-sans mt-1">
                Paired in {p.co_purchase_count}+ orders
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
